import { useEffect, useState } from 'react';
import { academicApi } from '../lib/api';

/** Structure académique : niveaux, cours, versions, prérequis, syllabus, programmes.
 *
 *  C'est le socle dont tout le reste dépend : sans cours ni programme, ni le
 *  parcours autonome ni les offres d'inscription n'ont de contenu.
 */
export function AcademicAdminPage() {
  const [levels, setLevels] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [versions, setVersions] = useState<any[]>([]);
  const [syllabi, setSyllabi] = useState<any[]>([]);
  const [prereqs, setPrereqs] = useState<any[]>([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedVersion, setSelectedVersion] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [programVersions, setProgramVersions] = useState<any[]>([]);
  const [requirements, setRequirements] = useState<any[]>([]);
  const [selectedPv, setSelectedPv] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => { reload(); }, []);
  useEffect(() => { if (selectedCourse) academicApi.versions(selectedCourse).then(setVersions).catch(() => setVersions([])); }, [selectedCourse]);
  useEffect(() => {
    if (!selectedVersion) return;
    academicApi.syllabi(selectedVersion).then(setSyllabi).catch(() => setSyllabi([]));
    academicApi.prerequisites(selectedVersion).then(setPrereqs).catch(() => setPrereqs([]));
  }, [selectedVersion]);
  useEffect(() => { if (selectedProgram) academicApi.programVersions(selectedProgram).then(setProgramVersions).catch(() => setProgramVersions([])); }, [selectedProgram]);
  useEffect(() => { if (selectedPv) academicApi.requirements(selectedPv).then(setRequirements).catch(() => setRequirements([])); }, [selectedPv]);

  async function reload() {
    try {
      const [l, c, p] = await Promise.all([
        academicApi.levels().catch(() => []),
        academicApi.courses().catch(() => []),
        academicApi.programs().catch(() => []),
      ]);
      setLevels(l); setCourses(c); setPrograms(p);
    } catch (e: any) { setError(String(e.message || e)); }
  }

  async function run(fn: () => Promise<any>, note: string) {
    setError(''); setNotice('');
    try { await fn(); setNotice(note); await reload(); }
    catch (e: any) { setError(String(e.message || e)); }
  }

  function ask(label: string, fallback = '') {
    return window.prompt(label, fallback) ?? '';
  }

  return <section className="page">
    <header className="page-header">
      <div>
        <span className="eyebrow">ACADÉMIQUE</span>
        <h1>Structure des enseignements</h1>
        <p>Niveaux, cours, versions, prérequis, syllabus et programmes.</p>
      </div>
    </header>

    {error && <div className="alert error">{error}</div>}
    {notice && <div className="notice">{notice}</div>}

    <div className="grid two">
      <div className="card">
        <h3>Niveaux ({levels.length})</h3>
        <button className="button secondary" onClick={() => {
          const code = ask('Code du niveau (ex. L1)'); if (!code) return;
          const title = ask('Intitulé') || code;
          run(() => academicApi.createLevel({ code, title, sequence: levels.length + 1 }), 'Niveau créé.');
        }}>Ajouter un niveau</button>
        <div className="member-list">
          {levels.map(l => <button key={l.id}><strong>{l.code}</strong><span>{l.title}</span></button>)}
          {!levels.length && <p className="technical-note">Aucun niveau.</p>}
        </div>
      </div>

      <div className="card">
        <h3>Cours ({courses.length})</h3>
        <button className="button secondary" disabled={!levels.length} onClick={() => {
          const code = ask('Code du cours (ex. BIO-101)'); if (!code) return;
          const title = ask('Intitulé') || code;
          run(() => academicApi.createCourse({ code, title, description: '' }), 'Cours créé.');
        }}>Ajouter un cours</button>
        {!levels.length && <p className="technical-note">Créez d'abord un niveau.</p>}
        <label>Cours sélectionné
          <select value={selectedCourse} onChange={(e: any) => { setSelectedCourse(e.target.value); setSelectedVersion(''); }}>
            <option value="">—</option>
            {courses.map(c => <option key={c.id} value={c.id}>{c.code} — {c.title}</option>)}
          </select>
        </label>
      </div>
    </div>

    {selectedCourse && <div className="card">
      <h3>Versions du cours</h3>
      <button className="button secondary" onClick={() => {
        const levelId = levels[0]?.id; if (!levelId) return;
        const credits = Number(ask('Crédits', '3')) || 3;
        run(() => academicApi.createVersion(selectedCourse, { level_id: levelId, version: versions.length + 1, credits }), 'Version créée.');
      }}>Ajouter une version</button>
      <label>Version sélectionnée
        <select value={selectedVersion} onChange={(e: any) => setSelectedVersion(e.target.value)}>
          <option value="">—</option>
          {versions.map(v => <option key={v.id} value={v.id}>v{v.version} · {v.credits} crédits</option>)}
        </select>
      </label>

      {selectedVersion && <div className="grid two">
        <div>
          <h4>Syllabus</h4>
          <button className="button secondary" onClick={() => {
            const title = ask('Titre du syllabus'); if (!title) return;
            run(() => academicApi.createSyllabus(selectedVersion, { version: syllabi.length + 1, title, description: '' }), 'Syllabus créé.');
          }}>Créer un syllabus</button>
          {syllabi.map(s => <div key={s.id} className="order-row">
            <strong>v{s.version} — {s.title}</strong>
            <span>{s.status}</span>
            {s.status !== 'PUBLISHED' && <button className="button primary" onClick={() =>
              run(() => academicApi.publishSyllabus(s.id), 'Syllabus publié.')}>Publier</button>}
          </div>)}
          {!syllabi.length && <p className="technical-note">Aucun syllabus.</p>}
        </div>

        <div>
          <h4>Prérequis</h4>
          <p className="technical-note">Ce sont eux qui ordonnent le parcours autonome.</p>
          <button className="button secondary" onClick={() => {
            const req = ask('Identifiant de la version de cours prérequise'); if (!req) return;
            run(() => academicApi.addPrerequisite(selectedVersion, { required_course_version_id: req }), 'Prérequis ajouté.');
          }}>Ajouter un prérequis</button>
          {prereqs.map(p => <div key={p.id} className="order-row">
            <strong>{(p.required_course_version_id || '').slice(0, 8)}</strong>
          </div>)}
          {!prereqs.length && <p className="technical-note">Aucun prérequis.</p>}
        </div>
      </div>}
    </div>}

    <div className="card">
      <h3>Programmes ({programs.length})</h3>
      <button className="button secondary" disabled={!levels.length} onClick={() => {
        const code = ask('Code du programme'); if (!code) return;
        const title = ask('Intitulé') || code;
        run(() => academicApi.createProgram({ level_id: levels[0].id, code, title }), 'Programme créé.');
      }}>Ajouter un programme</button>
      <label>Programme
        <select value={selectedProgram} onChange={(e: any) => { setSelectedProgram(e.target.value); setSelectedPv(''); }}>
          <option value="">—</option>
          {programs.map(p => <option key={p.id} value={p.id}>{p.code} — {p.title}</option>)}
        </select>
      </label>

      {selectedProgram && <>
        <button className="button secondary" onClick={() => {
          const credits = Number(ask('Crédits totaux', '60')) || 60;
          run(() => academicApi.createProgramVersion(selectedProgram, { version: programVersions.length + 1, total_credits: credits }), 'Version de programme créée.');
        }}>Ajouter une version</button>
        <label>Version du programme
          <select value={selectedPv} onChange={(e: any) => setSelectedPv(e.target.value)}>
            <option value="">—</option>
            {programVersions.map(v => <option key={v.id} value={v.id}>v{v.version} · {v.total_credits} crédits</option>)}
          </select>
        </label>
      </>}

      {selectedPv && <>
        <h4>Cours du programme ({requirements.length})</h4>
        <p className="technical-note">Identifiant à copier : <code>{selectedPv}</code> — c'est celui à saisir pour ouvrir un parcours autonome.</p>
        <button className="button secondary" onClick={() => {
          const cv = ask('Identifiant de la version de cours'); if (!cv) return;
          run(() => academicApi.addRequirement(selectedPv, { course_version_id: cv, required: true }), 'Cours rattaché.');
        }}>Rattacher un cours</button>
        {requirements.map(r => <div key={r.id} className="order-row">
          <strong>{(r.course_version_id || '').slice(0, 8)}</strong>
          <span>{r.required ? 'obligatoire' : 'optionnel'}</span>
        </div>)}
      </>}
    </div>
  </section>;
}
